<script setup lang="ts">
import { computed, type Ref, ref } from 'vue'
import type { DialogProps } from 'primevue/dialog'

import delay from '@/utils/delay'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import type { ButtonProps } from 'primevue'
import { useI18n } from 'vue-i18n'
import { isAFormValidationError } from '@/components/business/AForm/AFormValidationError'

export interface AModalPropsExtra {
  size?: 'small' | 'large'
  width?: string
  cancelText?: string
  confirmText?: string
  showConfirmBtn?: boolean
  showCancelBtn?: boolean
  confirmProps?: ButtonProps
  cancelProps?: ButtonProps
  confirm: (data: Any) => PromiseOptional<Any>
}

export type AModalProps = AModalPropsExtra & DialogProps

export type AModalSlotsScope = {
  loading: boolean
  onConfirm: () => Promise<Any>
}

export type AModalSlots = {
  default: (scope: { maximize: boolean; getRef: () => Ref }) => Any
  footer: (scope: AModalSlotsScope) => Any
  'footer-start': (scope: AModalSlotsScope) => Any
  'footer-cancel': (scope: AModalSlotsScope) => Any
  'footer-confirm': (scope: AModalSlotsScope) => Any
}

defineSlots<AModalSlots>()

const emits = defineEmits(['unmounted'])

const props = withDefaults(defineProps<AModalProps>(), {
  header: 'NO_UNSET',
  modal: true,
  closable: true,
  showHeader: true,
  showCancelBtn: true,
  showConfirmBtn: true,
})

const { t } = useI18n()

const { header, confirm, cancelText, confirmText, showCancelBtn, showConfirmBtn, ...resetProps } =
  props

const childrenRef = ref()
const visible = ref(true)
const loading = ref(false)
const maximize = ref(false)

const _width = computed(() => (maximize.value ? '100%' : props.width || '600px'))

const getChildrenRef = () => childrenRef
const onClose = () => (visible.value = false)
const onAfterLeave = () => delay(300).then(() => emits('unmounted'))

const onConfirm = async () => {
  if (loading.value) {
    return
  }

  loading.value = true

  try {
    const data = await childrenRef.value?.onSave?.()

    await confirm(data)
    onClose()
  } catch (error) {
    if (!isAFormValidationError(error)) {
      throw error
    }
  } finally {
    loading.value = false
  }
}

const onMaximize = () => (maximize.value = true)
const onUnMaximize = () => (maximize.value = false)
</script>

<template>
  <Dialog
    v-bind="resetProps"
    draggable
    auto-z-index
    v-model:visible="visible"
    @maximize="onMaximize"
    @unmaximize="onUnMaximize"
    @after-hide="onAfterLeave"
    :pt="{
      root: { class: 'a-modal-dialog min-w-120' },
      content: { class: 'py-6', style: { width: _width } },
      header: { class: 'py-1 border-b border-b-gray-200' },
      footer: { class: 'justify-between' },
    }"
  >
    <template #header>
      <div class="flex items-center flex-1">
        <div class="font-bold" :class="[size === 'small' ? 'text-base' : 'text-lg']">
          {{ header }}
        </div>
      </div>
    </template>

    <slot name="default" :maximize :getRef="getChildrenRef" />

    <template #footer>
      <slot name="footer" :loading :onConfirm="onConfirm">
        <slot name="footer-start" :loading :onConfirm="onConfirm">
          <div />
        </slot>

        <div class="flex items-center gap-2">
          <slot name="footer-cancel" :loading :onConfirm="onConfirm">
            <Button
              v-if="showCancelBtn"
              :size
              outlined
              :label="cancelText || t('cancel')"
              severity="secondary"
              @click="onClose"
              v-bind="cancelProps"
            />
          </slot>

          <slot name="footer-confirm" :loading :onConfirm="onConfirm">
            <Button
              v-if="showConfirmBtn"
              :size
              autofocus
              :loading="loading"
              :label="confirmText || t('confirm')"
              @click="onConfirm"
              v-bind="confirmProps"
            />
          </slot>
        </div>
      </slot>
    </template>
  </Dialog>
</template>

<style>
.a-modal-dialog .p-dialog-close-button {
  border: 0;
  background: transparent;
  color: var(--p-surface-500);
}

.a-modal-dialog .p-dialog-close-button:hover {
  background: transparent;
  color: var(--p-surface-700);
}
</style>
